import { Item } from '../../domain/item';
import { RapidCaptureRecord } from './rapidCaptureTypes';
import { isMatchingRapidCaptureItem } from './rapidCaptureReconciliation';

export interface CaptureActivityPresentation {
  group: 'waiting' | 'adding' | 'analysis' | 'failed' | 'confirming';
  label: string;
  detail?: string;
  severity: 'info' | 'warning';
  canRetry: boolean;
  retryLabel?: string;
  canDiscard: boolean;
  canOpenItem: boolean;
}
export const captureActivityPresentation = (
  record: RapidCaptureRecord,
  online: boolean,
  item?: Item,
): CaptureActivityPresentation => {
  const base = {
    severity: 'info' as const,
    canRetry: false,
    canDiscard: false,
    canOpenItem:
      record.state === 'confirming' && isMatchingRapidCaptureItem(record, item),
  };
  if (record.state === 'terminal')
    return {
      ...base,
      group: 'failed',
      label: 'Capture access changed',
      detail: record.lastError?.message,
      severity: 'warning',
      canDiscard: true,
    };
  if ((record as { schemaVersion: number }).schemaVersion !== 5)
    return {
      ...base,
      group: 'failed',
      label: 'Older queued capture',
      detail: record.lastError?.message,
      severity: 'warning',
      canRetry: online,
      retryLabel: 'Recover photo',
      canDiscard: true,
    };
  if (record.state === 'failed')
    return {
      ...base,
      group: 'failed',
      label: 'Capture needs attention',
      detail:
        record.aiSkipReason === 'cost-changed' &&
        record.pendingAiCredits !== undefined
          ? `Confirm ${record.pendingAiCredits} credits to generate keywords.`
          : record.lastError?.message,
      severity: 'warning',
      canRetry: online,
      retryLabel:
        record.aiSkipReason === 'cost-changed'
          ? record.pendingAiCredits === undefined
            ? 'Review cost'
            : 'Confirm & retry'
          : 'Retry',
      canDiscard: online || !record.itemCreateAttempted,
    };
  if (record.state === 'confirming')
    return { ...base, group: 'confirming', label: 'Item ready' };
  if (record.serverAccepted)
    return {
      ...base,
      group: 'analysis',
      label: record.analyzeImage ? 'Generating keywords' : 'Preparing item',
    };
  if (record.state === 'submitting')
    return { ...base, group: 'adding', label: 'Uploading' };
  return {
    ...base,
    group: 'waiting',
    label: online ? 'Waiting to upload' : 'Waiting for connection',
  };
};
